import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { dashboardService, DashboardData } from '../services/dashboardService';

const getModuleName = (modStr: string | number) => {
  const str = String(modStr);
  if (str === 'Módulo 1' || str === '1') return 'Módulo I: El Problema';
  if (str === 'Módulo 2' || str === '2') return 'Módulo II: El Insight';
  if (str === 'Módulo 3' || str === '3') return 'Módulo III: Dirección Creativa';
  if (str === 'Módulo 4' || str === '4') return 'Módulo IV: La Verdad Activadora';
  if (str === 'Módulo 5' || str === '5') return 'Módulo V: Estrategia de Integración';
  if (str === 'Módulo 6' || str === '6') return 'Módulo VI: Accionables Creativos';
  if (str === 'Módulo 10' || str === '10') return 'Módulo X: Bifurcación';
  if (str === 'Módulo 11' || str === '11') return 'Módulo Y: Subida de Información';
  if (str === 'Módulo 12' || str === '12') return 'Módulo Z: Diagnóstico';
  if (str === 'Módulo 13' || str === '13') return 'Módulo 6-B: Accionables Integrados';
  if (str === 'Módulo 0' || str === '0') return 'Onboarding';
  return str;
};

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await dashboardService.getDashboard();
        setData(res);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al cargar el dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Cargando...</div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--error)' }}>
          {error || 'No se pudo cargar el dashboard'}
        </div>
      </Layout>
    );
  }

  const metrics = [
    { label: 'Estrategias Activas', value: String(data.metrics.active_strategies), change: data.metrics.strategies_change > 0 ? `+${data.metrics.strategies_change} este mes` : '0 este mes' },
    { label: 'Clientes Registrados', value: String(data.metrics.total_clients), change: data.metrics.clients_change > 0 ? `+${data.metrics.clients_change} nuevos` : '0 nuevos' },
    { label: 'Briefs Completados', value: String(data.metrics.completed_briefs), change: `${data.metrics.completed_change}% éxito` },
    { label: 'Módulos Generados', value: String(data.metrics.generated_modules), change: `Promedio ${data.metrics.modules_change}/mes` },
  ];

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffH = Math.floor(diffMs / (1000 * 60 * 60));
    const diffD = Math.floor(diffH / 24);
    if (diffH < 1) return 'Hace menos de 1 hora';
    if (diffH < 24) return `Hace ${diffH} hora${diffH > 1 ? 's' : ''}`;
    if (diffD === 1) return 'Hace 1 día';
    return `Hace ${diffD} días`;
  };

  return (
    <Layout>
      <div>
        <h1 style={styles.pageTitle}>Dashboard</h1>
        <p style={styles.pageSubtitle}>Visión general de tu actividad estratégica</p>

        <div style={styles.metricsGrid}>
          {metrics.map((m, i) => (
            <div key={i} style={styles.metricCard}>
              <div style={styles.metricValue}>{m.value}</div>
              <div style={styles.metricLabel}>{m.label}</div>
              <div style={styles.metricChange}>{m.change}</div>
            </div>
          ))}
        </div>

        <div style={styles.twoColumn}>
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Actividad Reciente</h2>
            <div style={styles.activityList}>
              {(data.recent_activities || []).slice(0, 10).map((item) => (
                <div key={item.id} style={styles.activityItem}>
                  <div style={styles.activityDot} />
                  <div style={styles.activityContent}>
                    <div style={styles.activityAction}>{item.action}</div>
                    <div style={styles.activityMeta}>
                      {item.client_name} · {item.module_name} · {formatTime(item.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Progreso de Estrategias</h2>
            <div style={styles.progressList}>
              {(data.strategy_progress || []).map((item, i) => (
                <div key={i} style={styles.progressItem}>
                  <div style={styles.progressHeader}>
                    <span style={styles.progressClient}>{item.client_name}</span>
                    <span style={{
                      ...styles.progressStatus,
                      color: item.status === 'Finalizado' ? 'var(--success)' : 'var(--accent)'
                    }}>
                      {item.status}
                    </span>
                  </div>
                  <div style={styles.progressBarBg}>
                    <div style={{
                      ...styles.progressBarFill,
                      width: `${item.progress}%`,
                      background: item.status === 'Finalizado' ? 'var(--success)' : 'var(--accent)'
                    }} />
                  </div>
                  <div style={styles.progressModule}>{getModuleName(item.current_module)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '0.25rem',
  },
  pageSubtitle: {
    color: 'var(--text-secondary)',
    marginBottom: '1.5rem',
    fontSize: '0.95rem',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '1.25rem',
    marginBottom: '2rem',
  },
  metricCard: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius)',
    padding: '1.5rem',
    boxShadow: 'var(--shadow)',
    border: '1px solid var(--border)',
  },
  metricValue: {
    fontSize: '2.25rem',
    fontWeight: 800,
    marginBottom: '0.25rem',
    color: 'var(--text-primary)',
  },
  metricLabel: {
    fontSize: '0.9rem',
    color: 'var(--text-secondary)',
    marginBottom: '0.5rem',
  },
  metricChange: {
    fontSize: '0.8rem',
    color: 'var(--success)',
    fontWeight: 600,
  },
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '1.5rem',
  },
  section: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius)',
    padding: '1.5rem',
    boxShadow: 'var(--shadow)',
    border: '1px solid var(--border)',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '1.25rem',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
  },
  activityDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: 'var(--accent)',
    marginTop: '6px',
    flexShrink: 0,
  },
  activityContent: {
    flex: 1,
  },
  activityAction: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  activityMeta: {
    fontSize: '0.8rem',
    color: 'var(--text-muted)',
    marginTop: '0.15rem',
  },
  progressList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  progressItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressClient: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: 'var(--text-primary)',
  },
  progressStatus: {
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  progressBarBg: {
    height: '8px',
    background: 'var(--bg-hover)',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.5s ease',
  },
  progressModule: {
    fontSize: '0.8rem',
    color: 'var(--text-muted)',
  },
};

export default Dashboard;
